import PageLoader from "components/PageLoader"
import { useAppSelector } from "hooks/storeHooks"
import { getFontsTemplate, UICustomizationFonts } from "pages/UICustomization/UICustomizationFonts"
import { UICustomizationLogo } from "pages/UICustomization/UICustomizationLogo"
import {
  getStylesTemplate,
  UICustomizationStyles,
} from "pages/UICustomization/UICustomizationStyles"
import { UICustomizationWidgetPositioning } from "pages/UICustomization/UICustomizationWidgetPositioning"
import { UICustomizationData } from "pages/UICustomization/types.ts"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto.ts"
import { typedEnv } from "../../config/typedEnv"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  Link,
  LinkButton,
  Tab,
  TabGroup,
  TabPanel,
  TabPanels,
  Tabs,
} from "@strapi/design-system"
import { ArrowLeft, Check, Eye } from "@strapi/icons"
import { useAlert } from "providers/AlertProvider"
import { selectUserInfo } from "store/user/userSlice"
import { IntegrationType } from "types"

const getTabsTemplate = (t: (value: string) => string) => [
  {
    title: t("widgetPositioning.title"),
    subTitle: t("widgetPositioning.description"),
    key: "widgetPositioning",
    Component: UICustomizationWidgetPositioning,
  },
  {
    title: t("uiCustomization.logo"),
    subTitle: t("uiCustomization.yourCompanyLogo"),
    key: "logo",
    Component: UICustomizationLogo,
  },
  {
    title: t("uiCustomization.styles"),
    subTitle: t("uiCustomization.colorAndSizes"),
    key: "styles",
    Component: UICustomizationStyles,
  },
  {
    title: t("uiCustomization.fonts"),
    subTitle: t("uiCustomization.fontsCustomization"),
    key: "fonts",
    Component: UICustomizationFonts,
  },
  // {
  //   title: "Category order",
  //   subTitle: "Place Category in order which will display on the main page navigation",
  //   key: "categoryOrder",
  //   Component: UICustomizationCategoryOrder,
  // },
]

export const UICustomization = () => {
  const service = getTintVTOService()
  const { showAlert } = useAlert()
  const merchantId = useAppSelector((state) => state.user.userInfo.merchantId)
  const { t } = useTranslation()
  const { integrationType } = useAppSelector(selectUserInfo)

  const tabsTemplate = getTabsTemplate(t).filter((template) => {
    if (template.key === "logo" && integrationType === IntegrationType.TIENDANUBE) return false
    if (template.key === "widgetPositioning" && integrationType !== IntegrationType.TIENDANUBE)
      return false
    return true
  })
  const [data, setData] = useState<UICustomizationData>({
    logos: {
      main: undefined,
      short: undefined,
      mainBase64: undefined,
      shortBase64: undefined,
    },
    styles: {},
  })
  const [currentTab, setCurrentTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [validData, setValidData] = useState(
    tabsTemplate.reduce((acc, { key }) => ({ ...acc, [key]: true }), {})
  )
  const [saveLoading, setSaveLoading] = useState(false)
  const valid = useMemo(() => Object.values(validData).every((value) => value), [validData])
  const [_merchantName, setMerchantName] = useState("")

  useEffect(() => {
    if (!merchantId) return
    service
      .merchantById(merchantId)
      .then((merchantData) => {
        if (merchantData?.data?.merchantById?.name)
          setMerchantName(`"${merchantData.data.merchantById.name}"`)
      })
      .catch((error) => {
        console.error("Failed to fetch merchant data:", error)
      })
  }, [merchantId, service])

  const onSave = useCallback(() => {
    ;(async () => {
      setSaveLoading(true)

      try {
        // Save UI Customization (logos, styles, fonts)
        const responseJson = await service.updateUICustomization({
          merchantId,
          data: data.styles,
          mainLogo: data.logos.mainBase64,
          shortLogo: data.logos.shortBase64,
        })
        const error = responseJson.errors ? responseJson.errors[0] : false

        if (error) throw new Error(error.message)

        // Save Widget Positioning (if Tiendanube user and data exists)
        if (integrationType === IntegrationType.TIENDANUBE && data.widgetPositioning) {
          const widgetResponse = await fetch(
            `${typedEnv.VITE_TIENDANUBE_API_URL}/api/v1/merchant/button-display`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                merchantId,
                ...data.widgetPositioning,
              }),
            }
          )

          if (!widgetResponse.ok) throw new Error("Failed to save widget positioning")
        }

        showAlert({
          error: false,
          text: t("uiCustomization.saved"),
        })
      } catch (error: any) {
        showAlert({
          error: true,
          text: error.message || t("uiCustomization.saveError"),
        })
      } finally {
        setSaveLoading(false)
      }
    })()
  }, [data, merchantId, integrationType, setSaveLoading, showAlert, t, service])

  useEffect(() => {
    const fontsValid = getFontsTemplate(t).every(
      ({ keyFamily, keyStyle, keyWeight }) =>
        data.styles[keyFamily] !== "" &&
        data.styles[keyStyle] !== "" &&
        data.styles[keyWeight] !== "" &&
        data.styles[keyFamily] &&
        data.styles[keyStyle] &&
        data.styles[keyWeight]
    )

    let stylesValid = true
    getStylesTemplate(t).forEach(({ styles }) => {
      if (!styles.every(({ key }) => data.styles[key] !== "")) stylesValid = false
    })
    setValidData((prevValidData) => ({
      ...prevValidData,
      fonts: fontsValid,
      styles: stylesValid,
    }))
  }, [data.styles, t])

  useEffect(() => {
    ;(async () => {
      if (!merchantId) return

      setLoading(true)

      const mainLogo = await fetch(
        `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/logo.png`
      )
      const shortLogo = await fetch(
        `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/short-logo.png`
      )

      const {
        data: { getStyles },
      } = await service.getUICustomization(merchantId)
      setData((prevData) => ({
        ...prevData,
        styles: JSON.parse(getStyles),
        logos: {
          main:
            mainLogo.status === 200
              ? `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/logo.png`
              : undefined,
          short:
            shortLogo.status === 200
              ? `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/short-logo.png`
              : undefined,
          mainBase64: undefined,
          shortBase64: undefined,
        },
      }))

      setLoading(false)
    })()
  }, [merchantId, service])

  useEffect(() => {
    window.scrollTo({ top: 120, behavior: "smooth" })
  }, [currentTab])

  return (
    <Box>
      <Box
        style={{
          height: "120px",
          minWidth: "800px",
          marginBottom: "10px",
        }}
      />
      <Box
        background="neutral100"
        style={{
          position: "fixed",
          top: 0,
          zIndex: 2,
          padding: "24px 256px 24px 56px",
          height: "120px",
          minWidth: "800px",
          width: "100%",
        }}
      >
        <BaseHeaderLayout
          title={t("uiCustomization.uiCustomizationMerchantName")}
          subtitle={tabsTemplate[currentTab]?.subTitle || ""}
          as={"h1"}
          navigationAction={
            <Link startIcon={<ArrowLeft />} to={`/merchants`}>
              {t("uiCustomization.backToMerchants")}
            </Link>
          }
          primaryAction={
            <Flex gap={2}>
              <LinkButton
                startIcon={<Eye />}
                variant="tertiary"
                href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?merchantId=${merchantId}`}
              >
                {t("previewTintVto")}
              </LinkButton>
              <Button
                startIcon={<Check />}
                loading={saveLoading}
                onClick={onSave}
                disabled={!valid}
              >
                {t("uiCustomization.save")}
              </Button>
            </Flex>
          }
        />
      </Box>
      <Box
        style={{
          height: "60px",
          minWidth: "800px",
        }}
      />
      <ContentLayout
        style={{
          scrollPaddingTop: "180px",
          scrollBehavior: "smooth",
          paddingTop: "60px",
        }}
      >
        <TabGroup
          label={t("uiCustomization.tabsLabel")}
          id="tabs"
          onTabChange={(selected) => setCurrentTab(selected)}
          variant="simple"
          initialSelectedTabIndex={currentTab}
        >
          <Box
            background="neutral100"
            style={{
              position: "fixed",
              top: "120px",
              left: "286px",
              right: "256px",
              zIndex: 2,
              padding: "0 0 16px 0",
            }}
          >
            <Tabs>
              {tabsTemplate.map(({ title, key }) => (
                <Tab hasError={!validData[key]} key={key}>
                  {title}
                </Tab>
              ))}
            </Tabs>
          </Box>
          <Box style={{ marginTop: "60px" }}>
            <TabPanels>
              {tabsTemplate.map(({ Component, key }) => (
                <Box
                  paddingTop={4}
                  key={key}
                  style={{
                    scrollMarginTop: "180px",
                  }}
                >
                  <TabPanel color="neutral800" padding={4} background="neutral0">
                    {loading ? (
                      <PageLoader />
                    ) : (
                      <Component
                        data={data}
                        onChange={setData}
                        onSave={key === "widgetPositioning" ? onSave : undefined}
                        saveLoading={key === "widgetPositioning" ? saveLoading : undefined}
                      />
                    )}
                  </TabPanel>
                </Box>
              ))}
            </TabPanels>
          </Box>
        </TabGroup>
      </ContentLayout>
    </Box>
  )
}
